import {useContext, useEffect, useState} from 'react'
import {Checkbox, Col, Divider, Flex, Form, Modal, Row, Space} from 'antd'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import {notifyError} from '../../../../utils/notifications'
import {CognitoClientsContext, SET_SHOW_COGNITO_CLIENT_POPUP,} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'
import ProviderRows, {appendProviders, providerFormValues} from './ProviderRows'

const CognitoClientsFormModal = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(CognitoClientsContext)
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()
  const [modal, contextHolder] = Modal.useModal()
  const [roleOptions, setRoleOptions] = useState([])

  const selected = state?.selected_client || {}
  const isEditing = !!selected?.id

  const closeFormPopup = () => {
    dispatch({ type: SET_SHOW_COGNITO_CLIENT_POPUP, data: false })
    form.resetFields()
  }

  const invalidLogo = () =>
    notifyError(navDispatch, intl.formatMessage({ id: 'cognitoClients.logoInvalid' }))

  const getInitialValues = () => ({
    is_create: !isEditing,
    id: selected?.id || undefined,
    name: selected?.name || '',
    make: selected?.make || '',
    active: selected?.active ?? true,
    redirect_url: selected?.redirect_url || '',
    auth_url: selected?.auth_url || '',
    is_allowed_native_sign_in: selected?.is_allowed_native_sign_in ?? true,
    allowed_roles: selected?.allowed_roles
      ? selected.allowed_roles.split(',')
      : [],
    client_id: selected?.client_id || '',
    // Never sent back by the API; left blank, an edit keeps the stored secret
    client_secret: '',
    user_pool_id: selected?.user_pool_id || '',
    providers: providerFormValues(selected?.providers),
    custom_login_enabled: selected?.custom_login_enabled ?? false,
    email: '',
    password: '',
    first_name: '',
    last_name: '',
  })

  const fetchRoles = async () => {
    let res = await getRequest(`/api/role/`)
    // Loaded once when the form opens, not a search, so a toast is right here.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'generic.rolesLoadError' }))
      return
    }
    const results = res?.results || []
    setRoleOptions(
      results.map((item) => ({ value: item.name, label: item.name }))
    )
  }

  useEffect(() => {
    if (!state.show_cognito_client_popup) return
    fetchRoles()
  }, [state.show_cognito_client_popup])

  useEffect(() => {
    if (!state.show_cognito_client_popup) return
    form.setFieldsValue(getInitialValues())
  }, [state.show_cognito_client_popup])

  const saveCognitoClient = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'cognitoClients.save' }),
        showLoader: true,
      },
      notification: {},
    })

    // A make that does not exist yet is entered as free text, so trim it before
    // sending. Trimmed here rather than on change so multi word makes can be typed.
    const make = typeof values?.make === 'string' ? values.make.trim() : values?.make

    const formData = new FormData()
    Object.entries({ ...values, make }).forEach(([key, value]) => {
      if (key === 'providers') {
        // Sent separately below, with their logo files
      } else if (key === 'allowed_roles' && Array.isArray(value)) {
        formData.append(key, value.join(','))
      } else if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    // From the store rather than `values`, which only holds registered fields -
    // the rows also carry their id, logo file and preview
    appendProviders(formData, form.getFieldValue('providers'))

    const res = isEditing
      ? await putRequest(`/api/cognito_clients/${selected.id}/`, formData)
      : await postRequest(`/api/cognito_clients/setup/`, formData)

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    if (res?.status && Object.keys(res?.errors || {}).length === 0) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: isEditing
              ? 'cognitoClients.updateSuccess'
              : 'cognitoClients.saveSuccess',
          }),
        },
      })
      props.getCognitoClientsListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'cognitoClients.saveError' }),
        },
      })
    }
  }

  const confirmSave = () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'cognitoClients.confirmSetup' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'cognitoClients.confirmSetupContent' }),
      onOk: () => form.submit(),
      centered: true,
      maskClosable: true,
    })
  }

  return (
    <>
      {contextHolder}

      <Modal
        open={state.show_cognito_client_popup}
        title={
          isEditing
            ? intl.formatMessage(
                { id: 'cognitoClients.updateTitle' },
                { name: selected.name }
              )
            : intl.formatMessage({ id: 'cognitoClients.createTitle' })
        }
        width="60%"
        className="cognito-clients-form-modal"
        centered={true}
        footer={null}
        closable={true}
        onCancel={closeFormPopup}
      >
        <div className="container">
          <Form
            id="cognito-client-form"
            name="cognito-client-form"
            form={form}
            layout="vertical"
            onFinish={saveCognitoClient}
          >
            <InputText name="is_create" hidden={true} isRequired={false} />
            <InputText name="id" hidden={true} isRequired={false} />
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <InputText
                  name="name"
                  label={intl.formatMessage({ id: 'cognitoClients.name' })}
                  disabled={isEditing}
                  isRequired={true}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="make"
                  type="text"
                  label={intl.formatMessage({ id: 'cognitoClients.make' })}
                  isRequired={true}
                  disabled={isEditing}
                  allowClear={true}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="redirect_url"
                  label={intl.formatMessage({
                    id: 'cognitoClients.redirectUrl',
                  })}
                  isRequired={false}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="auth_url"
                  label={intl.formatMessage({ id: 'cognitoClients.authUrl' })}
                  isRequired={false}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="client_id"
                  label={intl.formatMessage({ id: 'cognitoClients.clientId' })}
                  isRequired={false}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="client_secret"
                  type="password"
                  label={intl.formatMessage({ id: 'cognitoClients.clientSecret' })}
                  placeholder={
                    isEditing
                      ? intl.formatMessage({ id: 'cognitoClients.clientSecretKeep' })
                      : intl.formatMessage({ id: 'cognitoClients.clientSecret' })
                  }
                  isRequired={false}
                />
              </Col>
              <Col span={12}>
                <InputText
                  name="user_pool_id"
                  label={intl.formatMessage({ id: 'cognitoClients.userPoolId' })}
                  placeholder="us-east-1_AbCdEf123"
                  isRequired={false}
                />
              </Col>
              <Col span={24}>
                <Divider orientation="left" plain={true} style={{ margin: '4px 0' }}>
                  {intl.formatMessage({ id: 'cognitoClients.providersSection' })}
                </Divider>
                <ProviderRows form={form} onInvalidLogo={invalidLogo} />
              </Col>
              <Col span={24}>
                <SelectInput
                  name="allowed_roles"
                  label={intl.formatMessage({
                    id: 'cognitoClients.allowedRoles',
                  })}
                  options={roleOptions}
                  placeholder={intl.formatMessage({
                    id: 'cognitoClients.allowedRoles',
                  })}
                  isMulti={true}
                  isRequired={false}
                  allowClear={true}
                  showSearch={true}
                  maxTagCount={5}
                />
              </Col>
              <Col span={24}>
                <Space size="large">
                  <Form.Item
                    name="active"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>
                      {intl.formatMessage({ id: 'cognitoClients.active' })}
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    name="is_allowed_native_sign_in"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>
                      {intl.formatMessage({ id: 'cognitoClients.nativeSignIn' })}
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    name="custom_login_enabled"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox data-testid="custom_login_enabled">
                      {intl.formatMessage({ id: 'cognitoClients.customLogin' })}
                    </Checkbox>
                  </Form.Item>
                </Space>
              </Col>
            </Row>
            {!isEditing && (
              <>
                <Divider orientation="left" plain={true}>
                  {intl.formatMessage({ id: 'cognitoClients.adminDetails' })}
                </Divider>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <InputText
                      name="email"
                      label={intl.formatMessage({
                        id: 'cognitoClients.email',
                      })}
                      type="text"
                      isRequired={true}
                    />
                  </Col>
                  <Col span={12}>
                    <InputText
                      name="password"
                      label={intl.formatMessage({
                        id: 'cognitoClients.password',
                      })}
                      type="password"
                      isRequired={true}
                    />
                  </Col>
                  <Col span={12}>
                    <InputText
                      name="first_name"
                      label={intl.formatMessage({
                        id: 'cognitoClients.firstName',
                      })}
                      isRequired={false}
                    />
                  </Col>
                  <Col span={12}>
                    <InputText
                      name="last_name"
                      label={intl.formatMessage({
                        id: 'cognitoClients.lastName',
                      })}
                      isRequired={false}
                    />
                  </Col>
                </Row>
              </>
            )}
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Flex justify="end" className="py-4">
                  <Button onClick={confirmSave} type="primary">
                    {intl.formatMessage({ id: 'cognitoClients.save' })}
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </>
  )
}

export default CognitoClientsFormModal
